import React, { useState, useEffect } from 'react';
import styles from './Header.module.scss';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import ChevronDropdownIcon from 'app/assets/figma/slideout/chevron-dropdown-old.svg';
import { MOMENT_TYPES, MOMENT_TYPE_CHANGE_MESSAGES } from 'utils/constants';
import { DeleteConfirmationDialog } from 'app/components/shared/DeleteConfirmationDialog/DeleteConfirmationDialog';

interface ChangeMomentTypeDropdownProps {
  momentType?: string;
  canEdit?: boolean;
  onMomentTypeChange?: (newType: string) => void;
}

/**
 * ChangeMomentTypeDropdown Component
 *
 * Displays the moment type as either:
 * - A readonly pill (for marketing moments, offers, promos, or when canEdit is false)
 * - An editable dropdown (for New Release, Product release types when canEdit is true)
 *
 * Phase 1: Readonly pill display ✅
 * Phase 2: Conditional readonly logic based on moment type ✅
 * Phase 3: Dropdown functionality with GenAI-style UI ✅
 */
export const ChangeMomentTypeDropdown: React.FC<
  ChangeMomentTypeDropdownProps
> = ({ momentType, canEdit = false, onMomentTypeChange }) => {
  // State for dropdown visibility and local moment type
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [localMomentType, setLocalMomentType] = useState(momentType);
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Sync local state with props when they change
  useEffect(() => {
    setLocalMomentType(momentType);
  }, [momentType]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown="moment-type"]')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // If no momentType, don't render anything
  if (!momentType) {
    return null;
  }

  // Available moment type options for the dropdown
  const momentTypeOptions = [
    MOMENT_TYPES.NEW_1_0_RELEASE,
    MOMENT_TYPES.MARKETING_MOMENT_AND_PRODUCT_RELEASE,
    MOMENT_TYPES.PRODUCT_RELEASE
  ];

  /**
   * Phase 2: Determine if the moment type should be readonly or editable
   *
   * Readonly conditions:
   * 1. canEdit is false (no edit permissions)
   * 2. momentType is one of: "Marketing Moment", "Offers", "Price Changes and Offers"
   *
   * Editable conditions (when canEdit is true):
   * - "New Release"
   * - "Product release with marketing"
   * - "Product release without marketing"
   */
  const isReadonly = (() => {
    // If no edit permissions, always readonly
    if (!canEdit) {
      return true;
    }

    // Normalize momentType for comparison (case-insensitive, trim whitespace)
    const normalizedType = momentType.toLowerCase().trim();

    // Marketing moments and offers are always readonly
    const alwaysReadonlyTypes = [
      MOMENT_TYPES.MARKETING_MOMENT.toLowerCase(),
      MOMENT_TYPES.OFFER_PRICE_PROGRAM.toLowerCase(),
      MOMENT_TYPES.PROMOS_OFFERS.toLowerCase()
    ];

    if (alwaysReadonlyTypes.includes(normalizedType)) {
      return true;
    }

    // For release types, editable when canEdit is true
    const editableReleaseTypes = [
      MOMENT_TYPES.NEW_1_0_RELEASE.toLowerCase(),
      MOMENT_TYPES.MARKETING_MOMENT_AND_PRODUCT_RELEASE.toLowerCase(),
      MOMENT_TYPES.PRODUCT_RELEASE.toLowerCase()
    ];

    if (editableReleaseTypes.includes(normalizedType)) {
      return false; // Editable
    }

    // For any other types, default to readonly for safety
    return true;
  })();

  /**
   * Determine if confirmation is needed and get appropriate message
   * Returns null if no confirmation needed, otherwise returns { title, message }
   */
  const getConfirmationMessage = (
    fromType: string | undefined,
    toType: string
  ): { title: string; message: string } | null => {
    if (!fromType) return null;

    const from = fromType.toLowerCase().trim();
    const to = toType.toLowerCase().trim();

    const newRelease = MOMENT_TYPES.NEW_1_0_RELEASE.toLowerCase();
    const withMarketing =
      MOMENT_TYPES.MARKETING_MOMENT_AND_PRODUCT_RELEASE.toLowerCase();
    const withoutMarketing = MOMENT_TYPES.PRODUCT_RELEASE.toLowerCase();

    // Case 1 & 2: Between New Release and Release with marketing - no confirmation
    if (
      (from === newRelease && to === withMarketing) ||
      (from === withMarketing && to === newRelease)
    ) {
      return null;
    }

    // Case 3 & 4: To Release without marketing - show removal warning
    if (to === withoutMarketing) {
      return MOMENT_TYPE_CHANGE_MESSAGES.REMOVE_MARKETING_DATE;
    }

    // Case 5 & 6: From Release without marketing - show note about auto-copy
    if (from === withoutMarketing) {
      return MOMENT_TYPE_CHANGE_MESSAGES.AUTO_POPULATE_MARKETING_DATE;
    }

    return null;
  };

  // Show confirmation dialog before changing moment type
  const handleMomentTypeClick = (newType: string) => {
    setDropdownOpen(false);

    const confirmationMessage = getConfirmationMessage(
      localMomentType,
      newType
    );

    // If no confirmation needed, proceed directly
    if (!confirmationMessage) {
      handleConfirmMomentTypeChange(newType);
      return;
    }

    // Show confirmation dialog with appropriate message
    setConfirmDialog({
      isOpen: true,
      title: confirmationMessage.title,
      message: confirmationMessage.message,
      onConfirm: () => handleConfirmMomentTypeChange(newType)
    });
  };

  // Handle confirmed moment type change with error handling
  const handleConfirmMomentTypeChange = async (newType: string) => {
    // Save the previous value for rollback on error
    const previousType = localMomentType;

    // Close dialog and optimistically update UI
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: () => {}
    });
    setLocalMomentType(newType);
    setIsUpdating(true);

    try {
      // Call the parent handler and await its completion
      if (onMomentTypeChange) {
        await onMomentTypeChange(newType);
      }
    } catch (error) {
      // Revert to previous value on error
      console.error(
        'Failed to update moment type, reverting to previous value:',
        error
      );
      setLocalMomentType(previousType);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle dialog cancellation
  const handleCancelMomentTypeChange = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: () => {}
    });
  };

  // Phase 3: Show readonly pill or editable dropdown based on conditions
  if (isReadonly) {
    // Readonly pill (no dropdown)
    return (
      <div
        className={styles.pill}
        style={{ cursor: 'default' }}
        onMouseEnter={e => {
          e.currentTarget.style.background = '#f8f8f8';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = '#f8f8f8';
        }}
      >
        <span className={styles.pillText}>{localMomentType}</span>
      </div>
    );
  }

  // Editable dropdown (matches GenAI dropdown styling)
  return (
    <div style={{ position: 'relative' }} data-dropdown="moment-type">
      <div
        className={styles.pill}
        style={{
          cursor: isUpdating ? 'not-allowed' : 'pointer',
          opacity: isUpdating ? 0.6 : 1
        }}
        onClick={() => !isUpdating && setDropdownOpen(!dropdownOpen)}
      >
        <span className={styles.pillText}>{localMomentType}</span>
        <span className={styles.pillChevron} aria-hidden="true">
          <img src={ChevronDropdownIcon} alt="" />
        </span>
      </div>

      {/* Dropdown Menu */}
      {dropdownOpen && !isUpdating && (
        <div className={styles.dropdownMenu} style={{ minWidth: '280px' }}>
          {momentTypeOptions.map(option => (
            <div
              key={option}
              className={styles.dropdownItem}
              onClick={() => handleMomentTypeClick(option)}
            >
              <span className={styles.dropdownItemText}>{option}</span>
              {localMomentType === option && (
                <BlackCheckMarkIcon width={18} height={14} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={handleCancelMomentTypeChange}
        confirmButtonText="Confirm"
      />
    </div>
  );
};

export default ChangeMomentTypeDropdown;
